<script lang="ts" setup>
import { z } from 'zod'
import type { FormErrorEvent, FormSubmitEvent } from '#ui/types'
import type { Section } from '~/types'

interface Props extends Pick<Section, 'title' | 'subtitle' | 'description'> {}

const props = defineProps<Props>()

const pb = useNuxtApp().$pb

const registerSchema = z.object({
  name: z.string().min(3, 'Username must be at least 3 characters long').max(150, 'Username must be under 150 characters long'),
  email: z.string().email(),
  password: z.string().min(8, 'Password must be at least 8 characters long'),
  passwordConfirm: z.string(),
  marketingOptIn: z.boolean(),
}).refine(data => data.passwordConfirm === data.password, { message: 'Passwords must match', path: ['passwordConfirm'],
})

type RegisterSchema = z.output<typeof registerSchema>

const toast = useToast()

const registerDetails = reactive({
  name: '',
  email: '',
  password: '',
  passwordConfirm: '',
  marketingOptIn: true,
})

const { validateTurnstileToken, token } = useTurnstile()

async function register(event: FormSubmitEvent<RegisterSchema>) {
  try {
    await validateTurnstileToken(token.value)
    await pb.collection('users').create(event.data)
    toast.add({
      id: 'register-success',
      title: 'Registration Successful',
      description: 'Login in progress...',
    })
    await pb.collection('users').authWithPassword(
      event.data.email,
      event.data.password,
    )
    navigateTo('/dashboard')
  }
  catch (e: any) {
    toast.add({
      id: 'register-error',
      title: 'Error',
      description: 'There was an error registering your account. Please try again or contact support.',
    })
    console.error(e)
  }
}

const passwordVisible = ref(false)
</script>

<template>
  <div class="bg-slate-50 shadow-inner">
    <UForm
      :schema="registerSchema"
      :state="registerDetails"
      class="max-w-sm mx-auto py-24 lg:py-32"
      autocomplete="off"
      @submit="register"
    >
      <HeadingText
        tag="h1"
        class="pb-6 text-center"
      >
        Register
      </HeadingText>
      <div
        class="htmldescription pb-12  max-w-4xl"
        data-allow-mismatch
        v-html="props.description"
      />
      <UFormGroup
        label="First Name"
        class="mb-3"
        name="name"
        size="lg"
      >
        <UInput
          v-model="registerDetails.name"
          type="text"
          placeholder="Name"
          size="lg"
        />
      </UFormGroup>
      <UFormGroup
        label="Email"
        class="mb-3"
        name="email"
        size="lg"
      >
        <UInput
          v-model="registerDetails.email"
          type="text"
          placeholder="Email"
          size="lg"
        />
      </UFormGroup>
      <UFormGroup
        label="Password"
        class="mb-3"
        name="password"
      >
        <UButtonGroup
          class="w-full"
          :ui="{ rounded: 'rounded-full' }"
          size="lg"
        >
          <UInput
            v-model="registerDetails.password"
            :type="passwordVisible ? 'string' : 'password'"
            placeholder="Password"
            class="flex-1"
            size="lg"
          />
          <UButton
            v-if="passwordVisible"
            tabindex="-1"
            type="button"
            icon="heroicons:eye-slash-16-solid"
            color="gray"
            size="lg"
            aria-label="hide password"
            @click="passwordVisible = !passwordVisible"
          />
          <UButton
            v-else
            tabindex="-1"
            type="button"
            icon="heroicons:eye-16-solid"
            color="gray"
            size="lg"
            aria-label="show password"
            @click="passwordVisible = !passwordVisible"
          />
        </UButtonGroup>
      </UFormGroup>
      <UFormGroup
        label="Confirm Password"
        class="mb-3"
        name="passwordConfirm"
      >
        <UButtonGroup
          class="w-full"
          :ui="{ rounded: 'rounded-full' }"
          size="lg"
        >
          <UInput
            v-model="registerDetails.passwordConfirm"
            size="lg"
            :type="passwordVisible ? 'string' : 'password'"
            placeholder="Password"
            class="flex-1"
          />
          <UButton
            v-if="passwordVisible"
            tabindex="-1"
            size="lg"
            type="button"
            icon="heroicons:eye-slash-16-solid"
            color="gray"
            aria-label="hide password"
            @click="passwordVisible = !passwordVisible"
          />
          <UButton
            v-else
            tabindex="-1"
            type="button"
            size="lg"
            icon="heroicons:eye-16-solid"
            color="gray"
            aria-label="show password"
            @click="passwordVisible = !passwordVisible"
          />
        </UButtonGroup>
      </UFormGroup>
      <UFormGroup
        label="Marketing Preferences"
        class="mb-3"
        name="marketing"
        description="Would you like to receive emails from us to keep you up to date with all things Grampian?"
      >
        <UToggle
          id="marketing"
          v-model="registerDetails.marketingOptIn"
          size="lg"
        />
      </UFormGroup>
      <NuxtTurnstile v-model="token" />
      <div class="flex justify-between items-center mt-6">
        <UButton
          type="button"
          to="/login"
          variant="link"
        >
          Login
        </UButton>
        <UButton
          type="submit"
          size="xl"
        >
          Register
        </UButton>
      </div>
    </UForm>
  </div>
</template>
